import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Plus,
  User,
  MapPin,
  Mail,
  Phone,
  Printer,
  Building2,
  Lock,
  Shield,
  Eye,
  EyeOff,
  Hash,
  Key,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { charLimitMessage, cn } from "@/lib/utils";
import { usePrescriptionData } from "@/lib/prescriptionData";
import {
  generatePrescriptionPDF,
  generatePrescriptionHTML,
  getDefaultPrescriptionData,
  type PrescriptionData,
} from "@/lib/prescriptionPdf";
import { Patient } from "@/services/patientService";
import SearchableDropdown from "@/components/AddPrescriptionItem/SearchableDropdown";
import { useTitanApp } from "@/hooks/useTitanApp";
import { ErrorModal } from "@/components/ErrorModal";
import { setPrescriptionImageBase64 } from "@/dispatchers/patientDispatchers";
import { getDrugInformation } from "@/dispatchers/nmsDispatchers";
import { getPatientPrescriptions } from "@/dispatchers/patientPrescriptionDispatcher";
import { Int32 } from "bson";
import { useDigitalCertificatesProfileData } from "@/lib/profile/digitalCertificatesProfileData";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";

interface PrescriptionModalProps {
  trigger?: React.ReactNode;
  onSave?: (prescription: any) => void;
  editMode?: boolean;
  patient?: Patient | null;
  existingPrescription?: {
    id: string;
    patientName: string;
    patientInitials: string;
    patientNHS: string;
    medication: string;
    dosage: string;
    quantity: string;
    duration: string;
    indication: string;
    notes?: string;
    priority: "urgent" | "routine";
    allergies?: string[];
    prescribedBy: string;
    medications: object[];
    pharmacyID: string;
    base64Image: string;
    prescriptionCategory: number;
  };
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onViewPrescription: (
    prescriptionImageUrl: string,
    prescriptionData?: any,
  ) => void;
  isDarkMode: boolean;
}

export function PrescriptionModal({
  trigger,
  onSave,
  editMode = false,
  patient,
  existingPrescription,
  isOpen,
  onOpenChange,
  onViewPrescription,
  isDarkMode,
}: PrescriptionModalProps) {
  // Each modal instance should have its own state to avoid conflicts
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [localOpen, setLocalOpen] = React.useState(false);
  const [medicationPopovers, setMedicationPopovers] = React.useState<
    Record<string, boolean>
  >({});
  const [prescriptionItems, setPrescriptionItems] = React.useState<any[]>([
    {
      id: Math.random().toString(36).substr(2, 9),
      medication: "",
      dosage: "",
      quantity: "",
      notes: "",
      repeatPrescription: false,
      medicationDispensed: false,
      contraindications: [],
      warnings: [],
      PrescriptionItemId: "",
    },
  ]);
  const { userDigitalCertificate } = useDigitalCertificatesProfileData();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isNewItemAdded, setIsNewItemAdded] = React.useState(false);
  const [prescriptionType, setPrescriptionType] = React.useState<
    "issue" | "approval" | null
  >(null);
  const [workflowStep, setWorkflowStep] = React.useState<
    "selection" | "signing" | "options" | "success"
  >("selection");
  const [selectedOption, setSelectedOption] = React.useState<
    "pharmacy" | "print" | ""
  >("");
  const [selectedPharmacy, setSelectedPharmacy] = React.useState<string>("");
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  const [selectedPrescriptionCategory, setSelectedPrescriptionCategory] =
    React.useState<"private" | "pgd" | "pharmacy-first">("private");
  // Digital signature state
  const [prescriptionPIN, setPrescriptionPIN] = React.useState<string>("");
  const [showPIN, setShowPIN] = React.useState<boolean>(false);
  const [signatureError, setSignatureError] = React.useState<string>("");
  const [isDigitallysigned, setIsDigitallyigned] =
    React.useState<boolean>(false);

  // Mock certificate data (in real app, this would come from user profile/API)
  const activeCertificate = {
    id: "cert-001",
    commonName: "Dr. Sarah Williams",
    serialNumber: "45A7B2C9D1E3F8G4",
    issueDate: "2024-01-15",
    expiryDate: "2026-01-15",
    status: "active" as const,
    fingerprint: "SHA-256: 2F:3A:8B:9C:1D:4E:5F:6A:7B:8C:9D:0E:1F:2A:3B:4C",
    issuer: "Medical Authority UK",
    purpose: "prescription" as const,
  };

  // Use external control for edit mode, local state for creation mode
  const open = editMode ? (isOpen ?? false) : localOpen;
  const setOpen = editMode ? (onOpenChange ?? setLocalOpen) : setLocalOpen;

  // Initialize with existing prescription data in edit mode
  React.useEffect(() => {
    if (!open) return; // only when dialog is open

    // prevent overwrite during signing or after API attempt
    if (
      workflowStep === "signing" ||
      workflowStep === "options" ||
      isNewItemAdded === true
    )
      return;

    if (editMode && existingPrescription) {
      const exsistingPharmacy = pharmacies.find(
        (p) => p.teamPharmacyID === existingPrescription.pharmacyID,
      );
      setPrescriptionImageBase64(existingPrescription.base64Image);
      setSelectedPharmacy(exsistingPharmacy?.teamPharmacyName || "");
      if (existingPrescription?.prescriptionCategory) {
        const mappedCategory =
          existingPrescription.prescriptionCategory === 1
            ? "private"
            : existingPrescription.prescriptionCategory === 2
              ? "pgd"
              : existingPrescription.prescriptionCategory === 3
                ? "pharmacy-first"
                : "private"; // default fallback

        setSelectedPrescriptionCategory(mappedCategory);
      }
      const medications = existingPrescription.medications || [];

      const prescriptionItems = medications.map((med: any) => ({
        id: med.id || Math.random().toString(36).substr(2, 9),
        medication: med.medication,
        dosage: med.dosage,
        quantity: med.quantity,
        notes: med.notes || "",
        repeatPrescription: false,
        medicationDispensed: false,
        contraindications: [],
        warnings: [],
        PrescriptionItemId: med.prescriptionItemId,
      }));
      setPrescriptionItems(prescriptionItems);
    } else if (!editMode) {
      setPrescriptionItems([
        {
          id: Math.random().toString(36).substr(2, 9),
          medication: "",
          dosage: "",
          quantity: "",
          notes: "",
          repeatPrescription: false,
          medicationDispensed: false,
          contraindications: [],
          warnings: [],
          PrescriptionItemId: "",
        },
      ]);
    }
  }, [editMode, existingPrescription, open, workflowStep]);

  const handleAddPrescriptionItem = () => {
    const newItem = {
      id: Math.random().toString(36).substr(2, 9),
      medication: "",
      dosage: "",
      quantity: "",
      notes: "",
      repeatPrescription: false,
      medicationDispensed: false,
      contraindications: [],
      warnings: [],
      PrescriptionItemId: "",
    };
    setPrescriptionItems([...prescriptionItems, newItem]);
    setIsNewItemAdded(true);
  };
  const numberToWords = (num) => {
    const ones = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
    ];

    const tens = [
      "",
      "",
      "twenty",
      "thirty",
      "forty",
      "fifty",
      "sixty",
      "seventy",
      "eighty",
      "ninety",
    ];

    if (num < 20) return ones[num];

    if (num < 100) {
      return `${tens[Math.floor(num / 10)]}${
        num % 10 ? " " + ones[num % 10] : ""
      }`;
    }

    if (num < 1000) {
      return `${ones[Math.floor(num / 100)]} hundred${
        num % 100 ? " " + numberToWords(num % 100) : ""
      }`;
    }

    return num.toString();
  };
  const numberToDoseText = (num) => {
    if (num === 1) return "one to be taken";
    if (num === 2) return "twice to be taken";
    if (num === 3) return "thrice to be taken";

    return `${numberToWords(num)} times to be taken`;
  };
  const handleUpdatePrescriptionItem = (
    id: string,
    updates: Partial<(typeof prescriptionItems)[0]>,
  ) => {
    const result =
      updates.dosage !== undefined ? { dosage: updates.dosage } : {};
    const hasDosage = Object.keys(result).length > 0;
    if (hasDosage) {
      let valueToSet;
      const endsWithSpace = updates.dosage.endsWith(" ");

      if (endsWithSpace) {
        const trimmedVal = updates.dosage.slice(0, -1);
        const words = trimmedVal.split(" ");
        const lastWord = words[words.length - 1];

        if (/^\d+$/.test(lastWord)) {
          words[words.length - 1] = numberToDoseText(parseInt(lastWord, 10));
          valueToSet = words.join(" ") + " ";
          updates.dosage = valueToSet;
        } else {
          const matched = doseShortCodeList?.find(
            (entry) =>
              entry?.shortCode?.toLowerCase() === lastWord.toLowerCase(),
          );

          if (matched) {
            words[words.length - 1] = matched.description;
            valueToSet = words.join(" ") + " ";
            updates.dosage = valueToSet;
          }
        }
      }
      setPrescriptionItems(
        prescriptionItems.map((item) =>
          item.id === id ? { ...item, ...updates } : item,
        ),
      );
    } else {
      setPrescriptionItems(
        prescriptionItems.map((item) =>
          item.id === id ? { ...item, ...updates } : item,
        ),
      );
    }

    setPrescriptionItems(
      prescriptionItems.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      ),
    );
  };

  const handleDeletePrescriptionItem = (id: string) => {
    setPrescriptionItems((items) => items.filter((item) => item.id !== id));
  };

  const handleUpdateMedication = (
    id: string,
    PrescriptionItemId: string,
    medicationName: string,
  ) => {
    const medication = medications.find((med) => med.name === medicationName);
    if (medication) {
      handleUpdatePrescriptionItem(id, {
        medication: medicationName,
        PrescriptionItemId: PrescriptionItemId,
        // contraindications: medication.contraindications,
        // warnings: medication.warnings,
      });
    }
  };

  const handleProceedWithIssue = () => {
    setWorkflowStep("signing");
  };

  const handleGenerateAndIssue = async () => {
    setIsProcessing(true);

    setIsProcessing(true);
    const transformedDrugs = prescriptionItems.map((item) => ({
      prescriptionItemId: item?.PrescriptionItemId,
      drugName: item.medication,
      quantity: item.quantity,
      dose: item.dosage,
      note: item.notes || "",
    }));

    const payload = {
      id: editMode
        ? existingPrescription?.id
        : Math.random().toString(36).substr(2, 9),
      prescriptionItems: transformedDrugs,
      prescriptionCategory: selectedPrescriptionCategory,
      status: "issued",
      pharmacy: selectedPharmacy,
      issuedAt: new Date().toISOString(),
    };

    await handleUpdatePrescription(payload);

    await getPatientPrescriptions(companyId);

    try {
      // Create prescription data
      const prescriptionData: PrescriptionData = {
        ...getDefaultPrescriptionData(),
        items: prescriptionItems,
        timestamp: new Date(),
        prescriptionCategory: selectedPrescriptionCategory,
      };

      // Generate and immediately issue the prescription
      // generatePrescriptionPDF(prescriptionData);

      // Save prescription with "issued" status
      if (onSave) {
        onSave({
          id: editMode
            ? existingPrescription?.id
            : Math.random().toString(36).substr(2, 9),
          prescriptionItems: transformedDrugs,
          prescriptionCategory: selectedPrescriptionCategory,
          status: "issued",
          pharmacy: selectedPharmacy,
          issuedAt: new Date().toISOString(),
        });
      }

      trackFirstTimeIntercomEvent({
        eventName: "prescription_approved_first_time",
        user: {
          id: currentUser.id,
          email: currentUser.email,
          name: currentUser.fullName,
        },
        properties: {
          source: "prescription_approval_flow",
        },
      });

      setSuccessMessage(`Prescription has been sent to ${selectedPharmacy}`);
      setWorkflowStep("success");
      setIsProcessing(false);
    } catch (error) {
      console.error("Error generating prescription:", error);
      setIsProcessing(false);
    }
  };

  const handlePrintPrescription = () => {
    setIsProcessing(true);

    try {
      const transformedDrugs = prescriptionItems.map((item) => ({
        prescriptionItemId: item?.PrescriptionItemId,
        drugName: item.medication,
        quantity: item.quantity,
        dose: item.dosage,
        note: item.notes || "",
      }));

      // Create prescription data
      const prescriptionData: PrescriptionData = {
        ...getDefaultPrescriptionData(),
        items: prescriptionItems,
        timestamp: new Date(),
        prescriptionCategory: selectedPrescriptionCategory,
      };

      // Generate prescription for printing
      generatePrescriptionPDF(prescriptionData);

      // Save prescription with "printed" status
      if (onSave) {
        onSave({
          id: editMode
            ? existingPrescription?.id
            : Math.random().toString(36).substr(2, 9),
          prescriptionItems: transformedDrugs,
          prescriptionCategory: selectedPrescriptionCategory,
          status: "printed",
          printedAt: new Date().toISOString(),
        });
      }

      setSuccessMessage(
        "Prescription has been generated and is ready to print",
      );
      setWorkflowStep("success");
      setIsProcessing(false);
    } catch (error) {
      console.error("Error generating prescription:", error);
      setIsProcessing(false);
    }
  };

  const handleGenerateForApproval = () => {
    setIsProcessing(true);

    try {
      const transformedDrugs = prescriptionItems.map((item) => ({
        prescriptionItemId: item?.PrescriptionItemId,
        drugName: item.medication,
        quantity: item.quantity,
        dose: item.dosage,
        note: item.notes || "",
      }));

      if (onSave) {
        onSave({
          id: editMode
            ? existingPrescription?.id
            : Math.random().toString(36).substr(2, 9),
          prescriptionItems: transformedDrugs,
          prescriptionCategory: selectedPrescriptionCategory,
          status: "pending_approval",
          submittedAt: new Date().toISOString(),
        });
      }

      setSuccessMessage("Prescription has been sent for approval");
      setWorkflowStep("success");
      setIsProcessing(false);
    } catch (error) {
      console.error("Error submitting prescription for approval:", error);
      setIsProcessing(false);
    }
  };

  const handleBackToSelection = () => {
    setWorkflowStep("selection");
    setPrescriptionType(null);
    setSelectedOption("");
    setSelectedPharmacy("");
    setPrescriptionPIN("");
    setSignatureError("");
    setIsDigitallyigned(false);
  };

  const handleBackToSigning = () => {
    setWorkflowStep("signing");
    setSelectedOption("");
    setSelectedPharmacy("");
    setIsDigitallyigned(false);
  };

  const handleDigitalSignature = async () => {
    setSignatureError("");

    // Validate PIN (in real app, this would verify against stored PIN)
    if (!prescriptionPIN) {
      setSignatureError("PIN is required for digital signature");
      return;
    }

    if (prescriptionPIN.length !== 4) {
      setSignatureError("PIN must be exactly 4 digits");
      return;
    }

    if (!/^\d+$/.test(prescriptionPIN)) {
      setSignatureError("PIN must contain only numbers");
      return;
    }

    // Simulate PIN verification (in real app, this would call an API)
    setIsProcessing(true);

    const result =
      await handleVerifyPinForPrescriptionApproval(prescriptionPIN);

    if (result === null || result === "False") {
      setIsProcessing(false);
      setSignatureError("PIN verification failed. Please try again");
      return;
    }

    setTimeout(() => {
      setIsDigitallyigned(true);
      setWorkflowStep("options");
      setIsProcessing(false);
    }, 1000);
  };

  const handleCloseModal = () => {
    setOpen(false);
    // Reset all modal state when closing
    setWorkflowStep("selection");
    setPrescriptionType(null);
    setSelectedOption("");
    setSelectedPharmacy("");
    setSuccessMessage("");
    setIsProcessing(false);
    setSelectedPrescriptionCategory("private");
    setMedicationPopovers({});
    setPrescriptionPIN("");
    setSignatureError("");
    setIsDigitallyigned(false);
  };

  const {
    setSearchTerm,
    searchTerm,
    medications,
    handleUpdatePrescription,
    handleVerifyPinForPrescriptionApproval,
    pharmaciesOptions,
    userVerification,
  } = usePrescriptionData();

  const filteredMedications = medications.filter((med) =>
    med.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const {
    appState,
    onboardingVerificationState,
    patientState,
    pharmacyServiceState,
  } = useTitanApp();
  const { userCertificate } = onboardingVerificationState;
  const { prescriptionImageBase64 } = patientState || {};
  const {
    currentPharmacy,
    currentPharmacyId,
    companyId,
    currentUser,
    pharmacies,
  } = appState || {};
  const { doseShortCodeList } = pharmacyServiceState || {};
  const { patientPrescription } = patientState || {};

  const handlePreviewPrescription = async () => {
    setIsProcessing(true);

    try {
      const transformedDrugs = prescriptionItems.map((item) => ({
        prescriptionItemId: item?.PrescriptionItemId,
        drugName: item.medication,
        quantity: item.quantity,
        strength: item.strength,
        medicationType: item.medicationType,
        dose: item.dosage,
        note: item.notes || "",
      }));

      const patientData =
        patient ||
        (existingPrescription
          ? {
              title: "",
              foreName: existingPrescription.patientName?.split(" ")[0] || "",
              surName:
                existingPrescription.patientName
                  ?.split(" ")
                  .slice(1)
                  .join(" ") || "",
              address: "",
              dob: "",
              email: "",
              phone: "",
              contacts: [],
              allergies:
                existingPrescription.allergies?.map((a) => ({
                  allergyName: a,
                })) || [],
            }
          : null);

      const previewData = {
        patient: patientData,
        patientName: existingPrescription?.patientName || patient?.name,
        patientNHS: existingPrescription?.patientNHS || patient?.nhsNumber,
        allergies:
          existingPrescription?.allergies ||
          patient?.allergies?.map((a) => a.name),
        prescriptionCategory:
          selectedPrescriptionCategory === "private"
            ? 1
            : selectedPrescriptionCategory === "pgd"
              ? 2
              : 3,
        // This will be passed as the Drugs parameter to getHtmlData
        medications: transformedDrugs,
      };

      onViewPrescription(
        editMode ? existingPrescription?.id : "new",
        previewData,
      );
    } catch (error) {
      console.error("Error preparing prescription preview:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(openState) => {
        if (!openState) {
          // When closing, reset all state
          setWorkflowStep("selection");
          setPrescriptionType(null);
          setSelectedOption("");
          setSelectedPharmacy("");
          setSuccessMessage("");
          setIsProcessing(false);
          setSelectedPrescriptionCategory("private");
          setMedicationPopovers({});
          setPrescriptionPIN("");
          setSignatureError("");
          setIsDigitallyigned(false);
        }
        setOpen(openState);
      }}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-semibold">
            {editMode
              ? `Edit Prescription - ${
                  existingPrescription?.patientName || patient?.name
                }`
              : "Create prescription"}
          </DialogTitle>
        </DialogHeader>

        {/* Patient Information Bar */}
        {(patient || (editMode && existingPrescription)) && (
          <div className="p-3 mx-6 mb-3 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-start gap-3">
              {/* Patient Avatar */}
              <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-medium text-blue-700 bg-blue-100 rounded-full">
                {patient
                  ? patient.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()
                  : existingPrescription?.patientInitials}
              </div>

              {/* Patient Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-blue-600" />
                  <h3 className="text-base font-semibold text-blue-900">
                    {patient?.name || existingPrescription?.patientName}
                  </h3>
                  {/* Inline Allergies Warning */}
                  {((patient?.allergies && patient.allergies.length > 0) ||
                    (existingPrescription?.allergies &&
                      existingPrescription.allergies.length > 0)) && (
                    <div className="flex items-center gap-1 ml-2 px-2 py-0.5 bg-red-100 border border-red-300 rounded text-xs">
                      <AlertTriangle className="flex-shrink-0 w-3 h-3 text-red-600" />
                      <span className="font-medium text-red-800">
                        Allergies:{" "}
                        {patient?.allergies
                          ? patient.allergies
                              .map((allergy) => allergy.name)
                              .join(", ")
                          : existingPrescription?.allergies?.join(", ")}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 text-xs md:grid-cols-4 gap-x-3 gap-y-1">
                  <div className="flex items-center gap-1 text-blue-700">
                    <span className="font-medium">NHS:</span>
                    <span className="truncate">
                      {patient?.nhsNumber || existingPrescription?.patientNHS}
                    </span>
                  </div>

                  {patient?.phone && (
                    <div className="flex items-center gap-1 text-blue-700">
                      <Phone className="flex-shrink-0 w-3 h-3" />
                      <span className="truncate">{patient.phone}</span>
                    </div>
                  )}

                  {patient?.email && (
                    <div className="flex items-center gap-1 text-blue-700">
                      <Mail className="flex-shrink-0 w-3 h-3" />
                      <span className="truncate">{patient.email}</span>
                    </div>
                  )}

                  {editMode && existingPrescription?.prescribedBy && (
                    <div className="flex items-center gap-1 text-blue-700">
                      <span className="font-medium">By:</span>
                      <span className="truncate">
                        {existingPrescription.prescribedBy}
                      </span>
                    </div>
                  )}
                </div>

                {patient?.address && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-blue-700">
                    <MapPin className="flex-shrink-0 w-3 h-3" />
                    <span className="truncate">{patient.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-1">
          {/* Left Panel - Prescription Items */}
          <div className=" flex-1 p-6 pt-0 overflow-y-auto max-h-[55vh] custom-scrollbar pl-6">
            <div className="space-y-6">
              {prescriptionItems.map((item, index) => (
                <div
                  key={item.id}
                  className="p-4 space-y-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">
                      Item {index + 1}
                    </h3>
                    {prescriptionItems.length > 1 && index != 0 && (
                      <button
                        type="button"
                        className="text-gray-400 transition-colors hover:text-red-600"
                        title="Delete item"
                        onClick={() => handleDeletePrescriptionItem(item.id)}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Medication Selection */}
                  <div className="space-y-2">
                    {/* <Label htmlFor={`medication-${item.id}`}>Medication</Label>
                    <Popover
                      open={medicationPopovers[item.id] || false}
                      onOpenChange={(open) =>
                        setMedicationPopovers((prev) => ({
                          ...prev,
                          [item.id]: open,
                        }))
                      }
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={medicationPopovers[item.id] || false}
                          className="justify-between w-full"
                        >
                          {item.medication || "Search medication..."}
                          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search medications..."
                            value={searchTerm}
                            onValueChange={(val) => {
                              setSearchTerm(val); // <-- must update state
                            }}
                          />

                          <CommandList>
                            <CommandEmpty>No medication found.</CommandEmpty>
                            <CommandGroup>
                              {filteredMedications.map((med) => (
                                <CommandItem
                                  key={med.name}
                                  value={med.name}
                                  onSelect={(currentValue) => {
                                    debugger;
                                    handleUpdateMedication(
                                      item.id,
                                      currentValue
                                    );
                                    setMedicationPopovers((prev) => ({
                                      ...prev,
                                      [item.id]: false,
                                    }));
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      item.medication === med.name
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {med.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {item.value && (
                      <div className="error-message">{item.value}</div>
                    )} */}

                    <div className="pc-modal-input-field med-field">
                      <label>Medication</label>
                      <SearchableDropdown
                        className={`searchable-dropdown ${""}`}
                        searchType="drug"
                        label="name"
                        id="id"
                        selectedVal={item.medication}
                        handleChange={(val) => {
                          handleUpdateMedication(
                            item.id,
                            val.prescribedItemId,
                            val.drugName,
                          );
                          // handleChange(item.id, "value", val, item);
                        }}
                        maxLength={500}
                      />
                      {/* {errors.value && (
                        <div className="error-message">{errors.value}</div>
                      )} */}
                    </div>
                  </div>

                  {/* Dosage and Quantity */}
                  <div className="">
                    <div className="space-y-2">
                      <Label htmlFor={`dosage-${item.id}`}>Dosage</Label>

                      <textarea
                        rows={4}
                        placeholder="e.g. Take 1 tablet twice daily"
                        maxLength={500}
                        id={`dosage-${item.id}`}
                        value={item.dosage}
                        onChange={(e) =>
                          handleUpdatePrescriptionItem(item.id, {
                            dosage: e.target.value,
                          })
                        }
                        className="
                          text-sm
                          w-full
                          rounded-[10px]
                          p-2
                          border-[var(--global-tabbed-content-panel-borders)]
                          border
                          text-[var(--para-clr)]
                          bg-[var(--grey-222)]
                          whitespace-pre-wrap
                          placeholder:text-sm
                          resize-y
                          break-words
                          focus:ring-1
                          focus:outline-none
                          disabled:opacity-50
                          focus:ring-[var(--global-tabbed-content-panel-borders)]
                          
                          disabled:cursor-not-allowed"
                      />

                      <div className="text-right text-xs text-[var(--para-clr)] mt-1">
                        {item?.dosage?.length || 0}/500
                      </div>
                      {(item?.dosage?.length || 0) >= 500 && (
                        <p className="text-sm text-red-600">
                          {charLimitMessage(500)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`quantity-${item.id}`}>Quantity</Label>
                      <Input
                        id={`quantity-${item.id}`}
                        value={item.quantity}
                        min={1}
                        step={1}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === ".") {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) =>
                          handleUpdatePrescriptionItem(item.id, {
                            quantity: e.target.value,
                          })
                        }
                        placeholder="e.g. 30 tablets, 100ml"
                      />
                    </div>
                  </div>

                  {/*Additional Notes */}

                  <div className="mt-5 pc-modal-input-field">
                    <label>Additional Notes</label>
                    <textarea
                      rows={5}
                      placeholder="Add any extra information for the patient"
                      maxLength={1000}
                      value={item?.notes}
                      onChange={(e) =>
                        handleUpdatePrescriptionItem(item.id, {
                          notes: e.target.value,
                        })
                      }
                      className="w-full text-sm p-2 rounded-[10px] border border-[var(--global-tabbed-content-panel-borders)] bg-[var(--grey-222)] text-[var(--para-clr)] placeholder:text-sm whitespace-pre-wrap break-words resize-y focus:outline-none focus:ring-1 focus:ring-[var(--global-tabbed-content-panel-borders)] disabled:opacity-50 disabled:cursor-not-allowed"
                    />

                    <div className="text-right text-xs text-[var(--para-clr)] mt-1">
                      {item?.notes?.length || 0}/1000
                    </div>
                    {(item?.notes?.length || 0) >= 1000 && (
                      <p className="text-sm text-red-600">
                        {charLimitMessage(1000)}
                      </p>
                    )}
                  </div>

                  {/* Checkboxes */}
                  {/* <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`repeat-${item.id}`}
                        checked={item.repeatPrescription}
                        onCheckedChange={(checked) =>
                          handleUpdatePrescriptionItem(item.id, {
                            repeatPrescription: !!checked,
                          })
                        }
                      />
                      <Label
                        htmlFor={`repeat-${item.id}`}
                        className="text-sm font-normal"
                      >
                        Repeat prescription
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`dispensed-${item.id}`}
                        checked={item.medicationDispensed}
                        onCheckedChange={(checked) =>
                          handleUpdatePrescriptionItem(item.id, {
                            medicationDispensed: !!checked,
                          })
                        }
                      />
                      <Label
                        htmlFor={`dispensed-${item.id}`}
                        className="text-sm font-normal"
                      >
                        Medication has been dispensed
                      </Label>
                    </div>
                  </div> */}

                  {/* Contraindications */}
                  {item.contraindications.length > 0 && (
                    <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="mb-1 text-sm font-medium text-yellow-800">
                            Contraindication:
                          </div>
                          <div className="space-y-1 text-sm text-yellow-700">
                            {item.contraindications.map((contra, idx) => (
                              <div key={idx}>{contra}</div>
                            ))}
                          </div>
                        </div>
                        <ChevronDown className="flex-shrink-0 w-4 h-4 text-yellow-600" />
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {item.warnings.length > 0 && (
                    <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="mb-1 text-sm font-medium text-yellow-800">
                            Warning:
                          </div>
                          <div className="text-sm text-yellow-700">
                            {item.warnings.join(" ")}
                          </div>
                        </div>
                        <ChevronDown className="flex-shrink-0 w-4 h-4 text-yellow-600" />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add More Button */}
              {!isDigitallysigned && (
                <Button
                  variant="ghost"
                  onClick={handleAddPrescriptionItem}
                  className="w-full h-16 text-gray-500 border-2 border-gray-300 border-dashed hover:text-gray-700 hover:border-gray-400"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add more
                </Button>
              )}
            </div>
          </div>

          {/* Right Panel - Actions */}
          <div className="p-6 border-l border-gray-200 w-80 bg-gray-50 overflow-y-auto max-h-[55vh] custom-scrollbar">
            <div className="space-y-6">
              {workflowStep === "selection" && (
                <>
                  <h3 className="font-semibold text-gray-900">
                    Prescription Options
                  </h3>

                  {/* Prescription Category Selection */}
                  <div className="space-y-3">
                    <Label>Select prescription type:</Label>

                    <Select
                      value={selectedPrescriptionCategory}
                      onValueChange={(value) =>
                        setSelectedPrescriptionCategory(
                          value as "private" | "pgd" | "pharmacy-first",
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select prescription type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">
                          Private Prescription
                        </SelectItem>
                        <SelectItem value="pgd">Private PGD</SelectItem>
                        <SelectItem value="pharmacy-first">
                          Pharmacy First
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Processing Method Selection */}
                  <div className="space-y-3">
                    <Label>Choose how to process this prescription:</Label>

                    <div className="space-y-2">
                      <Button
                        variant={
                          prescriptionType === "issue" ? "default" : "outline"
                        }
                        onClick={() => setPrescriptionType("issue")}
                        className="justify-start w-full h-auto p-4 text-left"
                        disabled={userDigitalCertificate === null}
                      >
                        <div>
                          <div className="font-medium">
                            Generate & Issue Prescription
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Immediately generate and issue prescription
                          </div>
                        </div>
                      </Button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4 space-y-3 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                      className="w-full"
                      disabled={isProcessing}
                    >
                      Cancel
                    </Button>

                    {prescriptionType === "issue" && (
                      <Button
                        onClick={handleProceedWithIssue}
                        disabled={
                          isProcessing ||
                          prescriptionItems.some(
                            (item) =>
                              !item.medication ||
                              !item.dosage ||
                              !item.quantity,
                          )
                        }
                        className="w-full text-white bg-green-600 hover:bg-green-700"
                      >
                        Continue
                      </Button>
                    )}

                    {prescriptionType === "approval" && (
                      <Button
                        onClick={handleGenerateForApproval}
                        disabled={
                          isProcessing ||
                          prescriptionItems.some(
                            (item) =>
                              !item.medication ||
                              !item.dosage ||
                              !item.quantity,
                          )
                        }
                        className="w-full text-white bg-blue-600 hover:bg-blue-700"
                      >
                        {isProcessing ? "Processing..." : "Submit for Approval"}
                      </Button>
                    )}
                  </div>
                </>
              )}

              {workflowStep === "signing" && (
                <>
                  <h3 className="font-semibold text-gray-900">
                    Digital Signature Required
                  </h3>

                  <div className="space-y-4">
                    {/* Certificate Information */}
                    <div
                      className={`p-4 rounded-lg ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Shield className="w-5 h-5 text-green-600" />
                        <div>
                          <h4
                            className={`font-medium ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                          >
                            Active Certificate
                          </h4>
                          <p className="text-sm text-green-700">
                            {currentUser?.fullName || ""}
                          </p>
                        </div>
                        <div className="ml-auto">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-green-900/40 text-green-300" : "bg-green-100 text-green-800"}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        </div>
                      </div>

                      <div
                        className={`text-xs space-y-1 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                      >
                        <p>
                          <span className="font-medium">Serial:</span>{" "}
                          {userCertificate?.serialNumber || ""}
                        </p>
                        <p>
                          <span className="font-medium">Expires:</span>{" "}
                          {new Date(
                            userCertificate?.expiryDate || "",
                          ).toLocaleDateString("en-GB")}
                        </p>
                        <p>
                          <span className="font-medium">Issuer:</span>{" "}
                          {userCertificate?.issuer || ""}
                        </p>
                        <p>
                          <span className="font-medium">Purpose:</span>{" "}
                          Prescription Authorisation
                        </p>
                      </div>
                    </div>

                    {/* PIN Input */}
                    <div className="space-y-3">
                      <Label htmlFor="prescription-pin">
                        <Hash className="inline w-4 h-4 mr-2" />
                        Enter your 4-digit PIN to authorise this prescription:
                      </Label>

                      <div className="relative">
                        <Input
                          id="prescription-pin"
                          type={showPIN ? "text" : "password"}
                          value={prescriptionPIN}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4);
                            setPrescriptionPIN(value);
                            setSignatureError("");
                          }}
                          placeholder="Enter 4-digit PIN"
                          maxLength={4}
                          className={`${
                            signatureError ? "border-red-500" : ""
                          } text-center text-lg tracking-widest`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPIN(!showPIN)}
                        >
                          {showPIN ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </Button>
                      </div>

                      {signatureError && (
                        <p
                          className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                        >
                          <AlertTriangle className="w-4 h-4" />
                          {signatureError}
                        </p>
                      )}
                    </div>

                    {/* Security Notice */}
                    <div
                      className={`p-3 rounded-lg ${isDarkMode ? "bg-amber-900/20 border border-amber-700" : "bg-amber-50 border border-amber-200"}`}
                    >
                      <div className="flex items-start gap-2">
                        <Key className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div
                          className={`text-xs ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                        >
                          <p className="mb-1 font-medium">
                            Prescription Authorisation
                          </p>
                          <p>
                            By entering your PIN, you are digitally signing this
                            prescription with your active certificate and taking
                            full clinical responsibility for its accuracy and
                            appropriateness.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    className={`space-y-3 pt-4 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                  >
                    <Button
                      variant="outline"
                      onClick={handleBackToSelection}
                      className="w-full"
                      disabled={isProcessing}
                    >
                      Back
                    </Button>

                    <Button
                      onClick={handleDigitalSignature}
                      disabled={
                        isProcessing ||
                        !prescriptionPIN ||
                        prescriptionPIN.length !== 4
                      }
                      className="w-full text-white bg-green-600 hover:bg-green-700"
                    >
                      {isProcessing ? "Verifying PIN..." : "Authorise with PIN"}
                    </Button>
                  </div>
                </>
              )}

              {workflowStep === "options" && (
                <>
                  <h3
                    className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                  >
                    Select Option
                  </h3>

                  {/* Digital Signature Confirmation */}
                  {isDigitallysigned && (
                    <div
                      className={`p-3 rounded-lg ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
                    >
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span
                          className={`text-sm font-medium ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                        >
                          Digitally signed by {currentUser?.fullName || ""}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Label>
                      Choose how you would like to process this prescription:
                    </Label>

                    {/* Option Selection */}
                    <div className="space-y-2">
                      <Button
                        variant={
                          selectedOption === "pharmacy" ? "default" : "outline"
                        }
                        onClick={() => setSelectedOption("pharmacy")}
                        className="justify-start w-full h-auto p-4 text-left whitespace-normal"
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className="flex-shrink-0 w-5 h-5" />
                          <div>
                            <div className="font-medium">Send to Pharmacy</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Send prescription directly to a selected pharmacy
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant={
                          selectedOption === "print" ? "default" : "outline"
                        }
                        onClick={handlePreviewPrescription}
                        className="justify-start w-full h-auto p-4 text-left whitespace-normal"
                      >
                        <div className="flex items-center gap-3">
                          <Eye className="flex-shrink-0 w-5 h-5" />
                          <div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Preview Prescription
                            </div>
                          </div>
                        </div>
                      </Button>
                    </div>

                    {/* Pharmacy Selection - Only show if pharmacy option is selected */}
                    {selectedOption === "pharmacy" && (
                      <div className="space-y-2">
                        <Label>Select pharmacy:</Label>
                        <Select
                          value={selectedPharmacy}
                          onValueChange={setSelectedPharmacy}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose pharmacy" />
                          </SelectTrigger>
                          <SelectContent>
                            {pharmaciesOptions.map((pharmacy) => (
                              <SelectItem key={pharmacy} value={pharmacy}>
                                {pharmacy}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div
                    className={`space-y-3 pt-4 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                  >
                    <Button
                      variant="outline"
                      onClick={handleBackToSigning}
                      className="w-full"
                      disabled={isProcessing}
                    >
                      Back
                    </Button>

                    {selectedOption === "pharmacy" && (
                      <Button
                        onClick={handleGenerateAndIssue}
                        disabled={isProcessing || !selectedPharmacy}
                        className="w-full text-white bg-green-600 hover:bg-green-700"
                      >
                        {isProcessing ? "Processing..." : "Send to Pharmacy"}
                      </Button>
                    )}

                    {selectedOption === "print" && (
                      <Button
                        onClick={handlePrintPrescription}
                        disabled={isProcessing}
                        className="w-full text-white bg-blue-600 hover:bg-blue-700"
                      >
                        {isProcessing ? "Processing..." : "Generate & Print"}
                      </Button>
                    )}
                  </div>
                </>
              )}

              {workflowStep === "success" && (
                <>
                  <h3
                    className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                  >
                    Success
                  </h3>

                  <div className="space-y-4">
                    <div
                      className={`rounded-lg p-4 ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
                    >
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span
                          className={`font-medium ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                        >
                          Success!
                        </span>
                      </div>
                      <p
                        className={`text-sm mt-2 ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                      >
                        {successMessage}
                      </p>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div
                    className={`pt-4 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                  >
                    <Button
                      onClick={handleCloseModal}
                      className="w-full text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Close
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <ErrorModal
        open={errorModalOpen}
        onOpenChange={setErrorModalOpen}
        title="Invalid PIN"
        message={"PIN verification failed. Please try again."}
        isDarkMode={false}
      />
    </Dialog>
  );
}
